import Paper from '@material-ui/core/Paper';
import { DateTime } from 'luxon';
import qs from 'query-string';
import React, { useEffect, useState } from "react";
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Alert, Card, CardFooter, CardHeader, Col, Container, Row, Spinner, Table
} from "reactstrap";
import CustomPagination from '../components/CustomPagination';
import Header from "../components/Headers/Header";
import { useGetDocStructurings } from '../hooks/useGetDocStructurings';
import { useQueryParams } from '../hooks/useQueryParams';
import { UserPermissions } from '../types';
import { secondsToTimeString } from '../utils/secondsToTimeString';
import { toCustomDateTimeString } from '../utils/toCustomDateTimeString';
import { toTitleCase } from '../utils/toTitleCase';
import { toTrimmedString } from '../utils/toTrimmedString';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { updateQueryParams } from '../utils/updateQueryParams';

const RESULTS_PER_PAGE = 10;

const DocStructuringExecs: React.FC = () => {
  const query = useQueryParams()
  const { data: user } = useCurrentUser()

  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  })
  const [searchTerm, setSearchTerm] = useState(() => {
    const searchTerm = query.get('searchTerm')
    if (searchTerm) {
      return searchTerm
    }
    return ''
  })
  const [searchUsername, setSearchUsername] = useState('')
  const queryClient = useQueryClient()
  const location = useLocation()
  const router = useHistory()

  const {
    isError,
    isLoading,
    data,
    refetch,
  } = useGetDocStructurings(pageTable, RESULTS_PER_PAGE, searchTerm, searchUsername)

  useEffect(() => {
    handleExecSearch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageTable])

  const handleExecSearch = () => {
    if (isLoading) {
      return
    }
    queryClient.removeQueries(["docStructurings", "all"])
    refetch()
  }

  const handlePressEnter = () => {
    setPageTable(1)
    handleExecSearch()
    const q = qs.parse(location.search)
    q.searchTerm = searchTerm
    if (searchTerm.trim().length === 0) {
      delete q.searchTerm
    }
    updateQueryParams(qs.stringify(q), router, location.pathname)
  }

  let content: JSX.Element | null = null

  if (isError) {
    content = (
      <Alert color="danger">
        Something went wrong!
      </Alert>
    );
  } else if (isLoading) {
    content = <Spinner />
  } else if (!data || data.data.length === 0) {
    content = (
      <Alert color="warning">
        No data available
      </Alert>
    );
  } else {
    content = (
      <>
        <Table className="align-items-center table-flush" responsive>
          <thead className="thead-light">
            <tr>
              <th scope="col">ID</th>
              <th scope="col">Name</th>
              <th scope="col">Input Files</th>
              <th scope="col">Output File</th>
              <th scope="col">Progress</th>
              <th scope="col">Status</th>
              <th scope="col">Executed By</th>
              <th scope="col">Execution Start Date/Time</th>
              <th scope="col">Time Taken</th>
            </tr>
          </thead>
          <tbody>
            {
              data.data.map((b, i) => (
                <tr key={i}>
                  <th scope="row">
                    {b.id}
                  </th>
                  <td title={b.name || ''}>
                    {toTrimmedString(b.name || '')}
                  </td>
                  <td>
                    {b.inputFiles && b.inputFiles.length > 0 ?
                      b.inputFiles.map((f, idx) => (
                        <a
                          key={idx}
                          href={`${f}?token=${localStorage.getItem('token')}`}
                          download
                          target='_blank'
                          rel='noreferrer'
                          style={{ marginRight: '8px' }}
                          title={`File ${idx + 1}`}
                        >
                          <i className="fas fa-download"></i>
                        </a>
                      ))
                      :
                      'Not available'
                    }
                  </td>
                  <td>
                    {
                      b.outputFile ?
                        <a href={`${b.outputFile}?token=${localStorage.getItem('token')}`} download target='_blank' rel='noreferrer'>
                          <i className="fas fa-download"></i>
                        </a>
                        :
                        'Not available'
                    }
                  </td>
                  <td style={{ minWidth: '180px' }}>
                    {(() => {
                      const total = typeof b.totalExec === 'number' ? b.totalExec : 0
                      const current = typeof b.currentExec === 'number' ? b.currentExec : 0
                      const fileCount = Array.isArray(b.inputFiles) ? b.inputFiles.length : 0
                      const isCompleted = b.status === 'completed'
                      const isError = b.status === 'error'
                      const isQueued = b.status === 'queued'
                      if (isQueued) {
                        return <span className="text-muted">Queued</span>
                      }
                      // The backend may split large PDFs into smaller chunks, so
                      // `total`/`current` are chunk counts (good for a smooth,
                      // page-weighted bar). We surface the % but label it in file
                      // terms so users aren't confused by a chunk count they never
                      // chose (e.g. "0/8" for a 3-file upload).
                      let percent = total > 0 ? Math.round((current / total) * 100) : 0
                      if (isCompleted) {
                        percent = 100
                      }
                      const fileLabel = fileCount > 0
                        ? `${fileCount} file${fileCount > 1 ? 's' : ''}`
                        : ''
                      // Map the smooth (chunk-based) percent onto the file count so
                      // the label reads in file terms the user understands, never
                      // the internal chunk count. Floor so a file is only shown as
                      // done when its work is fully complete (avoids "1/1" at 50%).
                      const filesDone = isCompleted
                        ? fileCount
                        : (fileCount > 0 ? Math.floor((percent / 100) * fileCount) : 0)
                      const barColor = isError ? '#f5365c' : isCompleted ? '#2dce89' : '#11cdef'
                      return (
                        <div title={fileLabel ? `${percent}% • ${fileLabel}` : `${percent}%`}>
                          <div
                            style={{
                              position: 'relative',
                              height: '18px',
                              width: '100%',
                              backgroundColor: '#e9ecef',
                              borderRadius: '4px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${percent}%`,
                                backgroundColor: barColor,
                                transition: 'width 0.4s ease',
                              }}
                            />
                            <span
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 600,
                                color: percent > 50 ? '#fff' : '#525f7f',
                              }}
                            >
                              {fileCount > 0 ? `${percent}% (${filesDone}/${fileCount})` : `${percent}%`}
                            </span>
                          </div>
                        </div>
                      )
                    })()}
                  </td>
                  <td>
                    {toTitleCase(b.status)}
                    {b.status === 'error' && b.message ?
                      <i
                        className="fas fa-exclamation-circle text-danger ml-2"
                        style={{ cursor: 'help' }}
                        title={b.message}
                      ></i>
                      : null
                    }
                  </td>
                  <td>{b.user}</td>
                  <td>{b.startedAt ? toCustomDateTimeString(DateTime.fromISO(b.startedAt)) : 'NA'}</td>
                  <td>
                    {
                      b.startedAt && b.endedAt ?
                        `${secondsToTimeString(DateTime.fromISO(b.endedAt).diff(DateTime.fromISO(b.startedAt), 'seconds').seconds)}`
                        :
                        `NA`
                    }
                  </td>
                </tr>
              ))
            }
          </tbody>
        </Table>
        <CardFooter className="py-4" style={{ textAlign: "center" }}>
          <nav style={{ display: "inline-block" }} aria-label="...">
            <CustomPagination
              activePage={pageTable}
              totalResults={data.total}
              resultsPerPage={RESULTS_PER_PAGE}
              onChange={setPageTable}
              label="Table navigation"
            />
          </nav>
        </CardFooter>
      </>
    )
  }
  return (
    <>
      <Header type='bulk' />
      <Container className="mt--7" fluid>
        <Row className="mt-5">
          <Col className="mb-5 mb-xl-0">
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row>
                  <Col>
                    <h2>Content Parser Execution</h2>
                  </Col>
                </Row>
                <Row className="align-items-center">
                  <div className='col-9'>
                    <div className="input-group-alternative input-group">
                      <div className="input-group-prepend">
                        <span onClick={handlePressEnter} className="input-group-text text-warning">
                          <i className="fas fa-search"></i>
                        </span>
                      </div>
                      <input
                        value={searchTerm}
                        onChange={e => {
                          setSearchTerm(e.target.value)
                        }}
                        onKeyDown={e => {
                          if (e.key.toLowerCase() === 'enter') {
                            handlePressEnter()
                          }
                        }}
                        placeholder="Search for any execution"
                        type="text"
                        className="form-control"
                      />
                    </div>
                  </div>
                  {
                    user?.roles.includes(UserPermissions.admin) &&
                    <div className="col-3">
                      <div className="input-group-alternative input-group">
                        <div className="input-group-prepend">
                          <span onClick={handlePressEnter} className="input-group-text text-warning">
                            <i className="fas fa-search"></i>
                          </span>
                        </div>
                        <input
                          value={searchUsername}
                          onChange={e => {
                            setSearchUsername(e.target.value)
                          }}
                          onKeyDown={e => {
                            if (e.key.toLowerCase() === 'enter') {
                              handlePressEnter()
                            }
                          }}
                          placeholder="Search by user's name"
                          type="text"
                          className="form-control"
                        />
                      </div>
                    </div>
                  }
                </Row>
              </CardHeader>
              <Paper className="px-4">
                {content}
              </Paper>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default DocStructuringExecs;
